import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';

const PAGE_SIZE = 20;

export const fetchPosts = createAsyncThunk(
  'posts/fetchPosts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { limit = PAGE_SIZE, page = 1, status, category } = params;
      const query = { limit, page };
      if (status) query.status = status;
      if (category) query.category = category;
      const { data } = await api.get('/posts', { params: query });
      return {
        posts: data.posts || [],
        total: data.total ?? 0,
        page: data.page ?? 1,
        limit: data.limit ?? PAGE_SIZE,
        stats: data.stats || null,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erreur lors du chargement des articles.');
    }
  }
);

export const fetchPostById = createAsyncThunk(
  'posts/fetchPostById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/posts/${id}`);
      return data.post;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Article non trouvé.');
    }
  }
);

export const createPost = createAsyncThunk(
  'posts/createPost',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/posts', payload);
      return data.post;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erreur lors de la création.');
    }
  }
);

export const updatePost = createAsyncThunk(
  'posts/updatePost',
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/posts/${id}`, payload);
      return data.post;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erreur lors de la mise à jour.');
    }
  }
);

export const deletePost = createAsyncThunk(
  'posts/deletePost',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/posts/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erreur lors de la suppression.');
    }
  }
);

const postsSlice = createSlice({
  name: 'posts',
  initialState: {
    list: [],
    total: 0,
    page: 0,
    hasMore: false,
    stats: null,
    current: null,
    loading: false,
    loadingMore: false,
    error: null,
  },
  reducers: {
    clearCurrent: (state) => {
      state.current = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetPostsList: (state) => {
      state.list = [];
      state.page = 0;
      state.total = 0;
      state.hasMore = false;
      state.stats = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPosts.pending, (state, action) => {
        const page = action.meta?.arg?.page ?? 1;
        if (page === 1) {
          state.loading = true;
        } else {
          state.loadingMore = true;
        }
        state.error = null;
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        const payload = action.payload;
        const posts = payload.posts || [];
        const page = payload.page ?? 1;
        const total = payload.total ?? 0;
        state.loading = false;
        state.loadingMore = false;
        if (page === 1) {
          state.list = posts;
        } else {
          const ids = new Set(state.list.map((p) => p._id));
          const appended = posts.filter((p) => !ids.has(p._id));
          state.list = state.list.concat(appended);
        }
        state.page = page;
        state.total = total;
        state.hasMore = state.list.length < total;
        if (payload.stats) state.stats = payload.stats;
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.payload;
      })
      .addCase(fetchPostById.fulfilled, (state, action) => {
        state.current = action.payload;
      })
      .addCase(createPost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
.addCase(createPost.fulfilled, (state, action) => {
      state.loading = false;
      state.list = [action.payload, ...state.list];
      state.total = state.total + 1;
    })
      .addCase(createPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePost.fulfilled, (state, action) => {
        state.loading = false;
        const i = state.list.findIndex((p) => p._id === action.payload._id);
        if (i >= 0) state.list[i] = action.payload;
        if (state.current?._id === action.payload._id) state.current = action.payload;
      })
      .addCase(updatePost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deletePost.fulfilled, (state, action) => {
        state.list = state.list.filter((p) => p._id !== action.payload);
        state.total = Math.max(0, state.total - 1);
        if (state.current?._id === action.payload) state.current = null;
      });
  },
});

export const { clearCurrent, clearError, resetPostsList } = postsSlice.actions;
export { PAGE_SIZE };
export default postsSlice.reducer;
